import React, { useState, useEffect, useCallback } from 'react'
import './Gallery.css'

// Media bucket base URL - configure via environment variable
const MEDIA_BASE_URL = import.meta.env.VITE_MEDIA_URL || 'https://wingbeat-dev-media.s3.eu-west-2.amazonaws.com'

/**
 * Gallery Component
 * Displays a thumbnail grid of images with lightbox overlay on click.
 * 
 * Props:
 *   - images: array - explicit image list [{src, alt, thumb?}]
 *   - title: string - optional gallery title
 *   - columns: number - grid columns (default 4)
 *   - maxImages: number - max images to show (default all)
 */
function Gallery({ images: propImages, title, columns = 4, maxImages }) {
  // Images come directly from props. (Listing an S3 bucket from the browser
  // isn't possible, so callers pass an explicit `images` array, e.g. via
  // ManifestGallery below.)
  const images = propImages || []
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [error] = useState(null)

  const navigateNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }, [images.length])

  const navigatePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }, [images.length])

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false)
  }, [])

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox()
          break
        case 'ArrowLeft':
          navigatePrev()
          break
        case 'ArrowRight':
          navigateNext()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [lightboxOpen, closeLightbox, navigatePrev, navigateNext])

  const openLightbox = (index) => {
    setCurrentIndex(index)
    setLightboxOpen(true)
  }

  // Handle click on backdrop to close
  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('lightbox-backdrop')) {
      closeLightbox()
    }
  }

  const displayImages = maxImages ? images.slice(0, maxImages) : images

  if (error) {
    return <div className="gallery-error">{error}</div>
  }

  if (images.length === 0) {
    return null // Don't render anything if no images
  }

  return (
    <div className="gallery-container">
      {title && <h3 className="gallery-title">{title}</h3>}

      <div
        className="gallery-grid"
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {displayImages.map((image, index) => (
          <div
            key={index}
            className="gallery-thumb"
            onClick={() => openLightbox(index)}
            role="button"
            tabIndex={0}
            aria-label={`View ${image.alt || `photo ${index + 1}`}`}
            onKeyDown={(e) => e.key === 'Enter' && openLightbox(index)}
          >
            <img
              src={image.thumb || image.src}
              alt={image.alt || `Photo ${index + 1}`}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {maxImages && images.length > maxImages && (
        <p className="gallery-more">
          +{images.length - maxImages} more photos
        </p>
      )}

      {/* Lightbox Overlay */}
      {lightboxOpen && (
        <div
          className="lightbox-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-label="Image lightbox"
        >
          <div className="lightbox-content">
            <button
              className="lightbox-close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              ×
            </button>

            <button
              className="lightbox-nav lightbox-prev"
              onClick={navigatePrev}
              aria-label="Previous image"
            >
              ‹
            </button>

            <div className="lightbox-image-container">
              <img
                src={images[currentIndex].src}
                alt={images[currentIndex].alt || `Photo ${currentIndex + 1}`}
              />
              {images[currentIndex].alt && (
                <div className="lightbox-caption">
                  {images[currentIndex].alt}
                </div>
              )}
            </div>

            <button
              className="lightbox-nav lightbox-next"
              onClick={navigateNext}
              aria-label="Next image"
            >
              ›
            </button>

            <div className="lightbox-counter">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Helper: Build image list from a gallery folder name
 * Uses the media bucket URL and a known file list.
 * 
 * Usage:
 *   const images = buildGalleryImages('cacrabank', ['photo1.jpg', 'photo2.jpg'])
 */
export function buildGalleryImages(galleryId, fileNames) {
  return fileNames.map(fileName => ({
    src: `${MEDIA_BASE_URL}/images/${galleryId}/${encodeURIComponent(fileName)}`,
    thumb: `${MEDIA_BASE_URL}/images/${galleryId}/thumbs/${encodeURIComponent(fileName)}`,
    alt: fileName.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ')
  }))
}

/**
 * Helper: Build image URL from a path relative to images/
 */
export function mediaUrl(path) {
  return `${MEDIA_BASE_URL}/images/${path}`
}

export { MEDIA_BASE_URL }

/**
 * ManifestGallery - self-loads a gallery folder's manifest.json from the media
 * CDN and renders the Gallery. Used to render Joomla {gallery}folder{/gallery}
 * shortcodes found in migrated flight-log / site content.
 *
 * Props:
 *   - folder: string, path under images/ (e.g. "flightlog/20260812-witchieknowe")
 *   - title: optional heading
 *   - columns: grid columns (default 4)
 */
export function ManifestGallery({ folder, title, columns = 4 }) {
  const [images, setImages] = useState([])
  const [state, setState] = useState('loading') // loading | ready | empty

  useEffect(() => {
    if (!folder) return
    let cancelled = false
    const manifestUrl = `${MEDIA_BASE_URL}/images/${folder.split('/').map(encodeURIComponent).join('/')}/manifest.json`
    fetch(manifestUrl)
      .then(r => (r.ok ? r.json() : []))
      .then(files => {
        if (cancelled) return
        if (Array.isArray(files) && files.length > 0) {
          setImages(buildGalleryImages(folder, files))
          setState('ready')
        } else {
          setState('empty')
        }
      })
      .catch(() => { if (!cancelled) setState('empty') })

    return () => { cancelled = true }
  }, [folder])

  // No folder -> nothing to show (never enters the loading state).
  if (!folder) return null
  if (state === 'loading') return <div className="gallery-loading">Loading gallery…</div>
  if (state === 'empty' || images.length === 0) return null

  return <Gallery images={images} title={title} columns={columns} />
}

export default Gallery
